import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  GetPaymentMethods as HttpV1GetPaymentMethods,
  PaymentGroup,
  PaymentMethod,
  PaymentMethodConfig,
  PaymentServiceProvider,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'

import type {
  GetPaymentMethods,
  PaymentGateway,
  PaymentMethodParameter,
  PaymentMethodVNext,
} from './getPaymentMethods.specs'

/**
 * Map new payment gateway to legacy payment service provider
 */
function mapGatewayToServiceProvider(
  gateway: PaymentGateway,
): PaymentServiceProvider {
  switch (gateway) {
    case 'PROCESSOUT':
      return 'processout'
    case 'ADYEN':
    default:
      return 'adyen'
  }
}

/**
 * Map new configuration parameter name to legacy config field name
 */
function mapPaymentMethodParameterNameToLegacyName(name: string) {
  switch (name) {
    case 'card.brand':
      return 'supportedBrands'
    case 'issuer':
      return 'issuers'
    default:
      return name
  }
}

function mapPaymentMethodParametersToLegacyConfig(
  parameters: PaymentMethodParameter[],
) {
  return parameters.reduce(
    (acc, parameter) => {
      if (parameter.values) {
        // eslint-disable-next-line no-param-reassign
        acc[mapPaymentMethodParameterNameToLegacyName(parameter.name)] =
          parameter.name === 'issuer'
            ? parameter.values.map(({ id, name }) => ({ id, name }))
            : parameter.values.map(({ name }) => name)
      }

      return acc
    },
    {} as Record<string, unknown>,
  )
}

/**
 * Map new payment error codes to the legacy `PaymentError` values.
 *
 * Most new error codes have no legacy equivalent and collapse to an empty
 * array, meaning the method stays visible and selectable (`enabled: true`).
 * Only price-related errors surface as the legacy `price_not_eligible` so
 * that existing error messaging (`PaymentMethodsErrors.vue`) keeps working.
 *
 * New → legacy mapping (PAYIN-4854):
 * - PRICE_TOO_HIGH / PRICE_TOO_LOW → ['price_not_eligible'] (collapsed)
 * - FEATURE_MISSING                → [] (method shown as enabled)
 * - PLATFORM_UNAVAILABLE           → [] (no legacy equivalent)
 * - COUNTRY_UNAVAILABLE            → [] (no legacy equivalent)
 * - INVALID_BILLING_COUNTRY        → [] (no legacy equivalent)
 * - BUSINESS_SCOPE_UNAVAILABLE     → [] (no legacy equivalent)
 * - UNSUPPORTED_PAYMENT_METHOD_BY_SELLERS → [] (no legacy equivalent)
 * - PAYMENT_METHOD_NOT_FOUND       → [] (no legacy equivalent)
 * - PAYMENT_METHOD_CODE_MISMATCH   → [] (no legacy equivalent)
 */
function mapPaymentMethodErrors(
  methodCode: string,
  errorsByMethod: GetPaymentMethods['response']['errors'],
) {
  const methodErrors = errorsByMethod[methodCode] ?? []

  return methodErrors.flatMap(({ code }) => {
    switch (code) {
      case 'PRICE_TOO_HIGH':
      case 'PRICE_TOO_LOW':
        return ['price_not_eligible']
      default:
        return []
    }
  })
}

function mapPaymentMethodVNextToLegacy(
  method: PaymentMethodVNext,
  errorsByMethod: GetPaymentMethods['response']['errors'],
  extraData: Pick<PaymentMethod, 'countryCode'>,
): PaymentMethod {
  const errors = mapPaymentMethodErrors(method.reference, errorsByMethod)

  return {
    reference: method.reference,
    bmCode: method.code,
    group:
      method.frontendConfiguration.group?.name ?? (method.code as PaymentGroup),
    networks: method.frontendConfiguration.icons,
    pspCode: mapGatewayToServiceProvider(method.gateway),
    features: method.features,
    config: {
      ...mapPaymentMethodParametersToLegacyConfig(method.parameters),
      // Country-specific frontend configuration is the source of truth for
      // legacy config fields such as `supportedBrands`.
      ...camelizeKeys(method.frontendConfiguration.values),
    } as PaymentMethodConfig,

    // A method is considered enabled when it has no blocking errors.
    enabled: errors.length === 0,
    errors: errors as PaymentMethod['errors'],

    // Deprecated fields no longer used (or barely used)
    promoCodeEnabled: true,
    autoCaptureEnabled: false,
    needMerchantAgreement: false,
    hppEnabled: false,
    architecture: 'finpay',
    integrationType: 'adyen_checkout_api',

    ...extraData,
  }
}

/**
 * Take the response from the new payment methods endpoint and map it to the old format.
 * /!\
 * - This will not work for Apple Pay methods.
 * - This will never provide a fraud partner.
 * @deprecated https://backmarket.atlassian.net/browse/PAYIN-4854
 */
export function mapGetPaymentMethodsResponseToLegacyResponse(
  response: GetPaymentMethods['response'],
  extraData: {
    countryCode: MarketCountryCode
  },
) {
  return {
    count: response.paymentMethods.length,
    next: null,
    previous: null,
    results: response.paymentMethods.map((method) =>
      mapPaymentMethodVNextToLegacy(method, response.errors, extraData),
    ),
  } satisfies HttpV1GetPaymentMethods.Response
}
